import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import { useAuth } from "../auth/AuthProvider";
import { MoneyMapApiClient, shouldRetryRequest } from "./client";

const ApiContext = createContext<MoneyMapApiClient | null>(null);

export function ApiProvider({ children }: { children: ReactNode }) {
  const { accessToken } = useAuth();
  const client = useMemo(
    () => (accessToken ? new MoneyMapApiClient(accessToken) : null),
    [accessToken],
  );
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: shouldRetryRequest,
            staleTime: 15_000,
            gcTime: 300_000,
            refetchOnWindowFocus: true,
          },
          mutations: {
            retry: false,
          },
        },
      }),
    [],
  );

  return (
    <ApiContext.Provider value={client}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </ApiContext.Provider>
  );
}

export function useApi(): MoneyMapApiClient {
  const client = useContext(ApiContext);
  if (!client) throw new Error("useApi requires an authenticated API session.");
  return client;
}
